import * as React from "react"
import { cn } from "@merels/ui"
import { MateBox } from "../MateBox/MateBox"
import type { Mate as MateType } from "../../list/MateList/MateList"

export interface MateCardProps {
  mate: MateType
  isOurMate: boolean
  botDisplayName?: string
  noBackground?: boolean
  /** Explicit tile side length in pixels, overriding the default 112px size (e.g. for the gameless video grid). */
  sizePx?: number
  /** Present only when this card is our own mate - opens the mate settings dialog. */
  onClick?: () => void
  /** Omni mode: present when this client may switch to acting as this mate. */
  onActAs?: () => void
  /** Present when this locally owned bot can be dismissed from the room. */
  onDismiss?: () => void
  /** Present when the room owner may change this mate's host status. */
  onToggleHost?: () => void
  /** Present when in gameless mode for self view, toggling minimization state. */
  onToggleSelfViewMinimized?: () => void
  isSelfViewMinimized?: boolean
}

export const MateCard = React.forwardRef<HTMLDivElement, MateCardProps>(
  ({
    mate,
    isOurMate,
    botDisplayName,
    noBackground = false,
    sizePx,
    onClick,
    onActAs,
    onDismiss,
    onToggleHost,
    onToggleSelfViewMinimized,
    isSelfViewMinimized,
  }, ref) => {
    const displayName = botDisplayName || mate.name

    return (
      <div
        ref={ref}
        className={cn(
          // w-fit so the card shrinks to the square MateBox (block divs are full-width by default).
          "w-fit rounded-lg overflow-hidden transition-colors",
        )}
      >
        <MateBox
          name={displayName}
          videoTrack={mate.videoTrack}
          audioTrack={mate.audioTrack}
          isLocalParticipant={mate.isLocalParticipant}
          videoControls={mate.videoControls}
          thumbnailUrl={mate.thumbnailUrl}
          isThinking={mate.isThinking}
          onDismiss={onDismiss}
          hostBadge={mate.hostBadge}
          hostToggleLabel={mate.hostToggleLabel}
          onToggleHost={onToggleHost}
          noBackground={noBackground}
          sizePx={sizePx}
          onClick={isOurMate ? onClick : onActAs}
          clickLabel={!isOurMate && onActAs ? `Act as ${displayName}` : undefined}
          isActive={mate.isActingAs}
          onToggleSelfViewMinimized={onToggleSelfViewMinimized}
          isSelfViewMinimized={isSelfViewMinimized}
        />
      </div>
    )
  }
)
MateCard.displayName = "MateCard"
